import i18next, { type i18n } from 'i18next';
import ICU from 'i18next-icu';
import type { Catalog } from './catalogs.ts';
import { defaultLanguage, formatLocale, type AnyLanguage, type Language } from './locales.ts';
import { pseudoLocalizeCatalog } from './pseudo.ts';

export type Resources = Partial<Record<Language, Catalog>>;

/**
 * Creates an isolated i18next instance (never the global one: the API renders for many recipients at once).
 * Messages use the ICU format: `{count, plural, one {# module} other {# modules}}`.
 */
export async function createTranslator(options: {
    language: AnyLanguage;
    resources: Resources;
    /** Adds the pseudo language, derived from the French catalogs. Development only. */
    pseudo?: boolean;
}): Promise<i18n> {
    const resources: Record<string, { translation: Catalog }> = {};
    for (const [language, catalog] of Object.entries(options.resources)) {
        resources[language] = { translation: catalog };
    }
    if (options.pseudo && options.resources[defaultLanguage]) {
        resources['pseudo'] = { translation: pseudoLocalizeCatalog(options.resources[defaultLanguage]) };
    }
    const instance = i18next.createInstance();
    // ICU plural and number rules follow the Intl locale of the language (the pseudo language formats like French).
    const icu = new ICU({
        memoize: true,
        parseLngForICU: (language) => formatLocale[language as AnyLanguage] ?? language,
    });
    await instance.use(icu).init({
        resources,
        lng: options.language,
        fallbackLng: defaultLanguage,
        supportedLngs: Object.keys(resources),
        interpolation: { escapeValue: false },
        returnNull: false,
        // A missing key shows itself instead of silently falling back to another text.
        parseMissingKeyHandler: (key) => `⚠ ${key}`,
    });
    return instance;
}
